<script setup lang="ts">
  import { onMounted, ref, reactive } from 'vue'
  import { useCookieStore } from '../stores/cookieStore'

  const cookieStore = useCookieStore()
  const showDetails = ref(false)

  // Estado temporal para las preferencias
  const tempPreferences = reactive({
    preferences: false,
    tracking: false,
  })

  onMounted(() => {
    cookieStore.initializeCookies()
    // Inicializar las preferencias temporales con los valores actuales
    tempPreferences.preferences = cookieStore.cookieConsent.preferences
    tempPreferences.tracking = cookieStore.cookieConsent.tracking
  })

  const toggleDetails = () => {
    showDetails.value = !showDetails.value
    // Resetear las preferencias temporales cuando se abre el panel
    if (showDetails.value) {
      tempPreferences.preferences = cookieStore.cookieConsent.preferences
      tempPreferences.tracking = cookieStore.cookieConsent.tracking
    }
  }

  const savePreferences = () => {
    cookieStore.updateCookieConsent('preferences', tempPreferences.preferences)
    cookieStore.updateCookieConsent('tracking', tempPreferences.tracking)
    showDetails.value = false
  }

  const cancelPreferences = () => {
    showDetails.value = false
  }
</script>

<template>
  <section
    v-if="!cookieStore.hasShownBanner"
    class="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t border-gray-200 p-4 z-50"
  >
    <div class="max-w-7xl mx-auto">
      <div class="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div class="flex-1">
          <h3 class="text-lg font-semibold text-gray-900 mb-2">Configuración de Cookies</h3>
          <p class="text-sm text-gray-600">
            Utilizamos cookies para mejorar tu experiencia en nuestro sitio web. Algunas son
            esenciales para el funcionamiento del sitio, mientras que otras nos ayudan a mejorar
            nuestros servicios.
            <router-link
to="/privacy-policy"
class="text-teal-600 hover:text-teal-700"
              >Leer aviso de privacidad</router-link
            >
          </p>
        </div>

        <div class="flex flex-col sm:flex-row gap-2">
          <button
            class="px-4 py-2 text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors"
            @click="toggleDetails"
          >
            Personalizar
          </button>
          <button
            class="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 transition-colors"
            @click="cookieStore.acceptAll"
          >
            Aceptar todo
          </button>
          <button
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            @click="cookieStore.rejectAll"
          >
            Rechazar todo
          </button>
        </div>
      </div>

      <!-- Panel de detalles -->
      <div v-if="showDetails" class="mt-4 p-4 bg-gray-50 rounded-lg">
        <div class="space-y-4">
          <!-- Cookies Esenciales -->
          <div class="flex items-center justify-between">
            <div>
              <h4 class="font-medium text-gray-900">Cookies Esenciales</h4>
              <p class="text-sm text-gray-600">
                Necesarias para el funcionamiento básico del sitio
              </p>
            </div>
            <div class="flex items-center">
              <span class="text-sm text-gray-500 mr-2">Siempre activas</span>
              <div class="w-12 h-6 bg-teal-600 rounded-full relative">
                <div class="w-4 h-4 bg-white rounded-full absolute right-1 top-1"></div>
              </div>
            </div>
          </div>

          <!-- Cookies de Preferencias -->
          <div class="flex items-center justify-between">
            <div>
              <h4 class="font-medium text-gray-900">Cookies de Preferencias</h4>
              <p class="text-sm text-gray-600">Guardan tus preferencias y mejoran la experiencia</p>
            </div>
            <div class="flex items-center">
              <label class="relative inline-flex items-center cursor-pointer">
                <input v-model="tempPreferences.preferences" type="checkbox" class="sr-only peer" />
                <div
                  class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"
                ></div>
              </label>
            </div>
          </div>

          <!-- Cookies de Tracking -->
          <div class="flex items-center justify-between">
            <div>
              <h4 class="font-medium text-gray-900">Cookies de Tracking</h4>
              <p class="text-sm text-gray-600">Nos ayudan a mejorar nuestros servicios</p>
            </div>
            <div class="flex items-center">
              <label class="relative inline-flex items-center cursor-pointer">
                <input v-model="tempPreferences.tracking" type="checkbox" class="sr-only peer" />
                <div
                  class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"
                ></div>
              </label>
            </div>
          </div>

          <!-- Botones de confirmación -->
          <div class="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-200">
            <button
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              @click="cancelPreferences"
            >
              Cancelar
            </button>
            <button
              class="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 transition-colors"
              @click="savePreferences"
            >
              Guardar preferencias
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
